import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";

import { ChevronLeftIcon, ChevronRightIcon } from "assets";

import { ViewerNavigationControlsProps } from "./types";
import {
  counterSx,
  fileNameSx,
  nextButtonSx,
  prevButtonSx,
  separatorSx,
} from "./styles";

const MODE_LABELS: Record<"document" | "entry", string> = {
  document: "Upload",
  entry: "entry",
};

export const ViewerNavigationControls: FunctionComponent<
  ViewerNavigationControlsProps
> = ({ fileName, navigationContext, activeTab, onNavigate }) => {
  const hasContext = !!navigationContext;
  const modeLabel = activeTab ? ` ${MODE_LABELS[activeTab]}` : "";

  const ids = useMemo(
    () => navigationContext?.ids ?? [],
    [navigationContext?.ids],
  );
  const currentIndex = navigationContext?.currentIndex ?? 0;
  const displayIndex = navigationContext?.displayIndex ?? currentIndex;
  const totalElements = navigationContext?.totalElements ?? 0;

  // True only when this is actually the first item across all server pages (no prev page to fetch).
  // fetchPrevPage being set means prior pages exist, so we can't be at the global first even if
  // displayIndex is 0 (e.g. doc-upload entry mode has no entry-level displayIndex).
  const isAtGlobalFirst =
    !hasContext ||
    (currentIndex === 0 && displayIndex === 0 && !navigationContext?.fetchPrevPage);
  // True when at position 0 in the currently loaded ids[], regardless of prior pages on the server.
  const isAtLoadedFirst = !hasContext || currentIndex === 0;
  const canFetchPrev =
    isAtLoadedFirst && !isAtGlobalFirst && !!navigationContext?.fetchPrevPage;
  const isFirst = isAtLoadedFirst && !canFetchPrev;

  const parentDocCtx = navigationContext?.parentDocumentContext;
  const prevParentDocCtx = navigationContext?.prevParentDocumentContext;

  // Global position for the counter: prefers displayIndex (server-based offset) over the local array index.
  const effectiveIndex = displayIndex ?? currentIndex;
  // How many items are covered from position 0 across all loaded pages: used to detect whether more pages exist.
  const totalLoaded = effectiveIndex - currentIndex + ids.length;

  const isAtLastEntry =
    navigationContext?.mode === "entry" && currentIndex === totalElements - 1;
  // True when at the last entry of a doc that has a next document to jump into.
  const hasParentDoc = isAtLastEntry && !!parentDocCtx;
  const isAtTrueEnd = !hasContext || effectiveIndex === totalElements - 1;
  // Combines two cases: reached last doc in a multi-doc list, or reached last item in a flat list.
  // Uses displayIndex (global offset) when available so the check works correctly across pages.
  const isLastDoc = hasParentDoc
    ? (parentDocCtx?.displayIndex ?? parentDocCtx?.currentIndex ?? 0) >=
      (parentDocCtx?.totalElements ?? 1) - 1
    : isAtTrueEnd;
  // True when more items exist on the server but haven't been fetched yet (flat list only).
  const hasMorePages =
    !hasParentDoc && hasContext && totalLoaded < totalElements;

  const singleEntryDocInfo = navigationContext?.singleEntryDocInfo;
  const counterIndex = singleEntryDocInfo
    ? singleEntryDocInfo.displayIndex
    : effectiveIndex;
  const counterTotal = singleEntryDocInfo
    ? singleEntryDocInfo.totalElements
    : totalElements;
  const tooltipText = singleEntryDocInfo
    ? `Document ${singleEntryDocInfo.displayIndex + 1} of ${
        singleEntryDocInfo.totalElements
      } · Single entry`
    : hasContext
    ? `${activeTab === "document" ? "Document" : "Entry"} ${
        effectiveIndex + 1
      } of ${totalElements}`
    : "";

  const isAtFirstEntry =
    navigationContext?.mode === "entry" && currentIndex === 0;
  const hasPrevParentDoc = isAtFirstEntry && !!prevParentDocCtx;
  const isPrevDisabled = !hasPrevParentDoc && !canFetchPrev && isFirst;

  const nextLabel = isAtLastEntry ? ` ${MODE_LABELS.document}` : modeLabel;
  const prevLabel = isAtFirstEntry ? ` ${MODE_LABELS.document}` : modeLabel;

  const [isFetchingNext, setIsFetchingNext] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const pendingNavRef = useRef<
    | { level: "entry" | "parentDoc"; index: number }
    | { level: "prevFetch"; prevIdsLength: number }
    | null
  >(null);

  // After a forward fetch completes, ids grows. Fire the pending navigation once the target index exists.
  useEffect(() => {
    if (pendingNavRef.current?.level === "entry") {
      const { index } = pendingNavRef.current;
      if (ids.length > index) {
        pendingNavRef.current = null;
        setIsFetchingNext(false);
        onNavigate?.(ids[index]);
      }
    }
  }, [ids, onNavigate]);

  // After a doc-level forward fetch completes, parentDocCtx.ids grows. Fire the pending cross-doc navigation.
  useEffect(() => {
    if (pendingNavRef.current?.level === "parentDoc" && parentDocCtx) {
      const { index } = pendingNavRef.current;
      if (parentDocCtx.ids.length > index) {
        pendingNavRef.current = null;
        setIsFetchingNext(false);
        onNavigate?.(parentDocCtx.ids[index]);
      }
    }
  }, [parentDocCtx, parentDocCtx?.ids, onNavigate]);

  // After a backward fetch completes, ids grows at the front (prepend). The target is the last of the
  // prepended items: ids[prepended - 1]. We detect completion by comparing against the pre-fetch length.
  useEffect(() => {
    if (pendingNavRef.current?.level === "prevFetch") {
      const { prevIdsLength } = pendingNavRef.current;
      if (ids.length > prevIdsLength) {
        const prepended = ids.length - prevIdsLength;
        pendingNavRef.current = null;
        setIsFetchingNext(false);
        onNavigate?.(ids[prepended - 1]);
      }
    }
  }, [ids, onNavigate]);

  // In doc-upload entry mode, a prev-page fetch doesn't change ids[] (the current doc's entries).
  // Instead, prevParentDocCtx becomes available once the prepended docs are in the store.
  useEffect(() => {
    if (pendingNavRef.current?.level === "prevFetch" && prevParentDocCtx) {
      pendingNavRef.current = null;
      setIsFetchingNext(false);
      onNavigate?.(prevParentDocCtx.targetEntryId);
    }
  }, [prevParentDocCtx, onNavigate]);

  const triggerFetch = (targetIndex: number) => {
    setIsFetchingNext(true);
    setFetchError(null);
    pendingNavRef.current = { level: "entry", index: targetIndex };
    navigationContext?.fetchNextPage?.().catch(() => {
      setIsFetchingNext(false);
      setFetchError("Failed to load. Try again.");
      pendingNavRef.current = null;
    });
  };

  const triggerParentDocFetch = (targetIndex: number) => {
    setIsFetchingNext(true);
    setFetchError(null);
    pendingNavRef.current = { level: "parentDoc", index: targetIndex };
    parentDocCtx?.fetchNextPage?.().catch(() => {
      setIsFetchingNext(false);
      setFetchError("Failed to load. Try again.");
      pendingNavRef.current = null;
    });
  };

  const triggerPrevFetch = () => {
    setIsFetchingNext(true);
    setFetchError(null);
    pendingNavRef.current = { level: "prevFetch", prevIdsLength: ids.length };
    navigationContext?.fetchPrevPage?.().catch(() => {
      setIsFetchingNext(false);
      setFetchError("Failed to load. Try again.");
      pendingNavRef.current = null;
    });
  };

  const handleRetry = () => {
    setFetchError(null);
    const nextIndex = currentIndex + 1;
    if (hasParentDoc && parentDocCtx && nextIndex >= parentDocCtx.ids.length) {
      triggerParentDocFetch(nextIndex);
    } else if (hasMorePages) {
      triggerFetch(nextIndex);
    }
  };

  const isNextDisabled = isFetchingNext || (isLastDoc && !hasMorePages);

  const handleNextClick = () => {
    if (hasParentDoc && parentDocCtx) {
      const nextDocIndex = parentDocCtx.currentIndex + 1;
      if (nextDocIndex < parentDocCtx.ids.length) {
        onNavigate?.(parentDocCtx.ids[nextDocIndex]);
      } else if (parentDocCtx.ids.length < parentDocCtx.totalElements) {
        triggerParentDocFetch(nextDocIndex);
      }
      return;
    }
    const nextIndex = currentIndex + 1;
    if (navigationContext && nextIndex < ids.length) {
      onNavigate?.(ids[nextIndex]);
      return;
    }
    if (hasMorePages) {
      triggerFetch(nextIndex);
    }
  };

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      {fileName && (
        <>
          <Typography sx={fileNameSx}>{fileName}</Typography>
          <Box sx={separatorSx} />
        </>
      )}
      <Button
        variant="text"
        sx={prevButtonSx}
        disabled={isPrevDisabled}
        startIcon={<ChevronLeftIcon />}
        onClick={() => {
          if (hasPrevParentDoc && prevParentDocCtx) {
            onNavigate?.(prevParentDocCtx.targetEntryId);
          } else if (canFetchPrev) {
            triggerPrevFetch();
          } else {
            const prevIndex = currentIndex - 1;
            if (prevIndex >= 0 && navigationContext) {
              onNavigate?.(navigationContext.ids[prevIndex]);
            }
          }
        }}
      >
        {`Prev${prevLabel}`}
      </Button>
      <Tooltip title={tooltipText} placement="top" arrow>
        <Box sx={counterSx}>
          {hasContext ? `${counterIndex + 1} / ${counterTotal}` : "– / –"}
        </Box>
      </Tooltip>
      {fetchError && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            color="error"
            variant="caption"
            sx={{ whiteSpace: "nowrap" }}
          >
            {fetchError}
          </Typography>
          <Button
            size="small"
            variant="text"
            sx={{
              minWidth: "auto",
              p: 0,
              textDecoration: "underline",
              fontSize: "inherit",
            }}
            onClick={handleRetry}
          >
            Retry
          </Button>
        </Box>
      )}
      <Button
        variant="text"
        sx={nextButtonSx}
        disabled={isNextDisabled}
        endIcon={<ChevronRightIcon />}
        onClick={handleNextClick}
      >
        {`Next${nextLabel}`}
      </Button>
    </Stack>
  );
};
